<template>
  <section class="interactions-report" aria-label="Interacciones con profesionales" :aria-busy="loading">
    <div class="report-heading">
      <div>
        <h3>Interacciones con profesionales</h3>
        <p>Visitas y clics registrados desde los perfiles públicos de Jobbing.</p>
      </div>
      <button type="button" class="action-button" :disabled="loading" @click="fetchReport">Actualizar</button>
    </div>

    <div class="report-toolbar">
      <label class="search-field" for="interaction-search">
        Buscar profesional
        <input id="interaction-search" v-model="search" type="search" maxlength="120" placeholder="Nombre, empresa o ID" />
      </label>
      <label for="interaction-sort">
        Ordenar por
        <select id="interaction-sort" v-model="sort">
          <option value="total">Todas las interacciones</option>
          <option value="views">Visitas al perfil</option>
          <option value="whatsapp_clicks">Clics a WhatsApp</option>
          <option value="social_clicks">Clics a redes sociales</option>
          <option value="website_clicks">Clics al sitio web</option>
        </select>
      </label>
      <label for="interaction-direction">
        Orden
        <select id="interaction-direction" v-model="direction">
          <option value="desc">De mayor a menor</option>
          <option value="asc">De menor a mayor</option>
        </select>
      </label>
    </div>
    <p class="scope-note">El período y la búsqueda se aplican a los totales, al gráfico y al detalle. Incluye profesionales de todos los planes y sin actividad registrada.</p>

    <p v-if="loading" class="status-message" role="status">Cargando interacciones…</p>
    <div v-else-if="error" class="error-message" role="alert">
      {{ error }} <button type="button" class="action-button" @click="fetchReport">Reintentar</button>
    </div>

    <template v-else-if="report">
      <p v-if="!report.tracking_enabled" class="tracking-notice">El registro de nuevas interacciones está deshabilitado. Se muestran los datos que ya fueron registrados.</p>

      <div class="kpi-grid">
        <article v-for="card in cards" :key="card.key" class="kpi-card" :class="card.key">
          <span>{{ card.label }}</span>
          <strong>{{ number(report.summary[card.key]) }}</strong>
          <small>{{ card.description }}</small>
        </article>
      </div>

      <div class="coverage-panel">
        <div><strong>{{ number(report.summary.professionals_with_activity) }}</strong><span>con interacciones</span></div>
        <div><strong>{{ number(report.summary.professionals_without_activity) }}</strong><span>sin interacciones registradas</span></div>
        <div><strong>{{ percent(report.summary.coverage) }}</strong><span>de {{ number(report.summary.professionals) }} profesionales</span></div>
      </div>

      <div class="chart-panel">
        <div class="chart-heading">
          <h4>Evolución diaria</h4>
          <span>Horario de Chile · {{ report.period.start_date }} al {{ report.period.end_date }}</span>
        </div>
        <div v-if="report.summary.total" class="chart-container">
          <LineChart :chart-data="chartData" :options="chartOptions" :styles="{ height: '300px', position: 'relative' }" />
        </div>
        <p v-else class="empty-message">No hay interacciones registradas para este período y búsqueda.</p>
        <div class="social-breakdown" aria-label="Clics por red social">
          <span v-for="network in report.social_networks" :key="network.kind">{{ network.label }} <strong>{{ number(network.clicks) }}</strong></span>
        </div>
      </div>

      <div class="table-heading">
        <h4>Detalle por profesional</h4>
        <span>{{ number(report.pagination.total) }} profesionales</span>
      </div>
      <div v-if="report.professionals.length" class="table-responsive">
        <table>
          <caption class="sr-only">Visitas y clics por profesional en el período seleccionado</caption>
          <thead><tr>
            <th scope="col">Profesional</th><th scope="col">Perfil</th><th scope="col">WhatsApp</th>
            <th scope="col">Redes sociales</th><th scope="col">Sitio web</th><th scope="col">Total</th><th scope="col">Última interacción</th>
          </tr></thead>
          <tbody>
            <tr v-for="pro in report.professionals" :key="pro.id">
              <td>
                <router-link :to="{ name: 'VerProfesional', params: { id: pro.id } }">{{ pro.name || `Profesional #${pro.id}` }}</router-link>
                <small v-if="pro.company_name">{{ pro.company_name }}</small>
                <small>#{{ pro.id }}<template v-if="pro.visibility !== 'public'"> · {{ visibilityLabel(pro.visibility) }}</template></small>
              </td>
              <td>{{ number(pro.views) }}</td><td>{{ number(pro.whatsapp_clicks) }}</td>
              <td>{{ number(pro.social_clicks) }}<small v-if="pro.social_clicks">{{ socialDetail(pro) }}</small></td><td>{{ number(pro.website_clicks) }}</td>
              <td><strong>{{ number(pro.total) }}</strong></td><td>{{ formatDate(pro.last_event_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty-message">No se encontraron profesionales para esta búsqueda.</p>
      <div v-if="report.pagination.total" class="pagination-controls">
        <button type="button" class="action-button" :disabled="report.pagination.page <= 1" @click="changePage(-1)">Anterior</button>
        <span>Página {{ report.pagination.page }} de {{ report.pagination.pages }}</span>
        <button type="button" class="action-button" :disabled="report.pagination.page >= report.pagination.pages" @click="changePage(1)">Siguiente</button>
      </div>
      <p class="counting-note">Las visitas al perfil y los clics a WhatsApp cuentan una vez por visitante y profesional al día (UTC). El sitio web y las redes sociales cuentan cada clic. Un clic a WhatsApp no confirma un mensaje ni una contratación. Los clics del antiguo campo «Sitio web o Instagram» se conservan en sitio web. Solo se muestra la actividad registrada desde que se habilitó la medición.</p>
    </template>
  </section>
</template>

<script>
import axios from 'axios';
import LineChart from '@/components/charts/LineChart.js';

export default {
  name: 'ProfessionalInteractionsReport',
  components: { LineChart },
  props: { startDate: { type: String, required: true }, endDate: { type: String, required: true } },
  data() {
    return {
      report: null, loading: false, error: null, search: '', sort: 'total', direction: 'desc', page: 1,
      reloadTimer: null, requestSerial: 0,
      cards: [
        { key: 'views', label: 'Visitas al perfil', description: 'Visitas registradas por día', color: '#2775bd' },
        { key: 'whatsapp_clicks', label: 'Clics a WhatsApp', description: 'Aperturas del enlace de contacto', color: '#16866a' },
        { key: 'social_clicks', label: 'Clics a redes sociales', description: 'Instagram, Facebook y X', color: '#8a53b6' },
        { key: 'website_clicks', label: 'Clics al sitio web', description: 'Aperturas del enlace del sitio', color: '#bc7422' },
      ],
    };
  },
  computed: {
    chartData() {
      return { labels: this.report.daily.map(row => row.date), datasets: this.cards.map(card => ({
        label: card.label, data: this.report.daily.map(row => row[card.key]),
        borderColor: card.color, backgroundColor: card.color, fill: false, lineTension: 0.15, pointRadius: 2,
      })) };
    },
    chartOptions() {
      return { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' },
        tooltips: { mode: 'index', intersect: false },
        scales: { xAxes: [{ ticks: { maxTicksLimit: 12 } }], yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] } };
    },
  },
  watch: { startDate: 'scheduleFetch', endDate: 'scheduleFetch', search: 'scheduleFetch', sort: 'scheduleFetch', direction: 'scheduleFetch' },
  mounted() { this.fetchReport(); },
  beforeDestroy() { clearTimeout(this.reloadTimer); this.requestSerial += 1; },
  methods: {
    scheduleFetch() {
      this.requestSerial += 1;
      this.page = 1;
      this.report = null;
      this.loading = true;
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => this.fetchReport(), 250);
    },
    async fetchReport() {
      clearTimeout(this.reloadTimer);
      const serial = ++this.requestSerial;
      this.report = null;
      this.error = null;
      if (!this.startDate || !this.endDate || this.startDate > this.endDate) {
        this.error = 'Selecciona un rango de fechas válido.';
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`${window.hostname || ''}api/admin/professional_interactions`, {
          params: { start_date: this.startDate, end_date: this.endDate, search: this.search.trim(),
            page: this.page, sort: this.sort, direction: this.direction },
        });
        if (serial === this.requestSerial) {
          this.report = response.data;
          this.page = response.data.pagination.page;
        }
      } catch (error) {
        if (serial === this.requestSerial) this.error = error.response?.data?.error || error.response?.data?.message || 'No se pudieron cargar las interacciones. Intenta actualizar.';
      } finally {
        if (serial === this.requestSerial) this.loading = false;
      }
    },
    changePage(offset) { this.page += offset; this.fetchReport(); },
    number(value) { return new Intl.NumberFormat('es-CL').format(value || 0); },
    percent(value) { return value == null ? '—' : new Intl.NumberFormat('es-CL', { style: 'percent', maximumFractionDigits: 1 }).format(value); },
    formatDate(value) { return value ? new Date(value).toLocaleString('es-CL', { timeZone: 'America/Santiago', dateStyle: 'short', timeStyle: 'short' }) : 'Sin actividad'; },
    visibilityLabel(value) { return ({ hidden: 'Oculto', suspended: 'Suspendido' })[value] || value; },
    socialDetail(pro) {
      return this.report.social_networks.filter(network => pro.social_clicks_by_network[network.kind])
        .map(network => `${network.label}: ${this.number(pro.social_clicks_by_network[network.kind])}`).join(' · ');
    },
  },
};
</script>

<style scoped>
.interactions-report { color: #243746; }
.report-heading, .chart-heading, .table-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
h3 { font-size: 24px; margin: 0 0 8px; }
h4 { font-size: 17px; margin: 0; }
p { color: #637785; font-size: 13px; line-height: 1.6; }
.action-button { border: 1px solid #c4d6df; border-radius: 7px; padding: 9px 14px; background: white; color: #176b84; cursor: pointer; font-size: 13px; }
.action-button:disabled { opacity: .5; cursor: default; }
.report-toolbar { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; margin: 18px 0 10px; }
.report-toolbar label { display: flex; flex-direction: column; gap: 6px; color: #526a79; font-size: 12px; font-weight: 600; margin: 0; }
.search-field { flex: 1 1 250px; }
input, select { border: 1px solid #c4d6df; border-radius: 6px; padding: 9px 12px; background: white; color: #243746; font-size: 14px; min-height: 40px; }
.scope-note { margin-bottom: 24px; }
.kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
.kpi-card { display: flex; flex-direction: column; gap: 10px; padding: 20px; border: 1px solid #dce6ec; border-top: 3px solid #2775bd; border-radius: 9px; }
.kpi-card.whatsapp_clicks { border-top-color: #16866a; }.kpi-card.social_clicks { border-top-color: #8a53b6; }.kpi-card.website_clicks { border-top-color: #bc7422; }
.kpi-card span { color: #526a79; font-size: 13px; }.kpi-card strong { font-size: 29px; }.kpi-card small { color: #70828e; font-size: 12px; }
.coverage-panel { display: flex; flex-wrap: wrap; gap: 24px; padding: 20px; margin: 18px 0 24px; background: #f2f8fa; border-radius: 9px; }
.coverage-panel > div { display: flex; align-items: baseline; gap: 9px; }.coverage-panel strong { font-size: 20px; color: #176b84; }.coverage-panel span { font-size: 13px; color: #526a79; }
.chart-panel { padding: 20px; border: 1px solid #dce6ec; border-radius: 9px; margin-bottom: 26px; }.chart-heading { margin-bottom: 20px; }
.chart-heading > span, .table-heading > span { color: #70828e; font-size: 12px; }.chart-container { min-width: 0; }
.social-breakdown { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 20px; }.social-breakdown > span { background: #f2f4f8; border-radius: 20px; padding: 7px 13px; font-size: 13px; }.social-breakdown strong { margin-left: 10px; }
.table-heading { margin-bottom: 14px; }.table-responsive { overflow-x: auto; border: 1px solid #dce6ec; border-radius: 8px; }
table { width: 100%; min-width: 850px; border-collapse: collapse; }th, td { padding: 13px; border-bottom: 1px solid #edf2f6; text-align: right; font-size: 13px; white-space: nowrap; }
th { background: #f5f8fa; color: #526a79; font-size: 12px; }th:first-child, td:first-child { text-align: left; min-width: 210px; white-space: normal; }
td a { color: #176b84; font-weight: 600; }td small { display: block; color: #70828e; margin-top: 3px; }tbody tr:last-child td { border-bottom: 0; }tbody tr:hover { background: #fbfdff; }
.pagination-controls { display: flex; justify-content: flex-end; align-items: center; gap: 14px; padding-top: 16px; font-size: 13px; }
.counting-note { margin: 24px 0 0; font-size: 12px; }.status-message, .empty-message { text-align: center; padding: 40px 20px; }
.error-message, .tracking-notice { padding: 15px; border-radius: 7px; background: #fff5e5; color: #815c25; }.error-message { background: #fff1f1; color: #9b3b3b; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (max-width: 1100px) { .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .kpi-grid { grid-template-columns: 1fr; }.report-toolbar label { flex: 1 1 100%; }.chart-panel { padding: 14px; }.pagination-controls { justify-content: center; } }
</style>
